// src/components/pages/VideoInteractions.js

import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  doc,
  getDoc,
  collection,
  getDocs,
} from "firebase/firestore";
import { auth, db } from "../../firebase";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Container,
  Button,
} from "@mui/material";
import {
  checkAndAddVideo,
  getArtSale,
} from "../../services/socialVideoService";
import VideoPlayerWithProgressBar from "../organisms/VideoPlayerWithProgressBar";
import AddInteractionModal from "../templates/AddInteractionModal";
import { useTheme } from "styled-components";
import InteractionItem from "../molecules/InteractionItem";

const VideoInteractions = () => {
  const location = useLocation();
  const theme = useTheme();
  const { videoId } = useParams();
  const [isOwner, setIsOwner] = useState(null); // Track ownership
  const [loading, setLoading] = useState(true); // Track loading state
  const [videoExists, setVideoExists] = useState(null); // Track if video exists
  const [videoData, setVideoData] = useState(null); // Store video data
  const [interactions, setInteractions] = useState([]); // Store interactions
  const [openModal, setOpenModal] = useState(false); // Control modal visibility
  const [currentTime, setCurrentTime] = useState(0); // Track current time
  const [initialTimestamp, setInitialTimestamp] = useState(0); // Track initial timestamp for modal
  const [error, setError] = useState(null); // Track errors

  useEffect(() => {
    const verifyOrAddVideo = async () => {
      try {
        // Fetch the video document from Firebase
        const videoRef = doc(db, "videos", videoId);
        const videoDoc = await getDoc(videoRef);

        if (videoDoc.exists()) {
          // Video exists in Firebase
          setVideoExists(true);
          const data = videoDoc.data();
          setVideoData(data);
          const currentUserId = auth.currentUser?.uid;

          // Check if the current user is the owner of the video
          if (data.userId === currentUserId) {
            setIsOwner(true);
            // Fetch interactions from the subcollection
            const interactionsRef = collection(
              db,
              "videos",
              videoId,
              "interactions"
            );
            const interactionsSnapshot = await getDocs(interactionsRef);
            const interactionsData = interactionsSnapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));
            // Sort interactions by timestamp
            interactionsData.sort((a, b) => a.timestamp - b.timestamp);
            setInteractions(interactionsData);
          } else {
            setIsOwner(false);
          }
        } else {
          // Video does not exist in Firebase
          console.log("The video does not exist in Firebase.");
          setVideoExists(false);

          // Handle the missing video scenario
          const { saleId } = location.state || {};

          if (!saleId) {
            console.warn("saleId not found in location.state");
            setError("Sale ID is missing. Cannot retrieve video data.");
            return;
          }

          // Call getArtSale to retrieve art sale data from the 3rd party API
          const artSaleResponse = await getArtSale(videoId, saleId);

          if (!artSaleResponse) {
            console.error("Invalid artSaleResponse received from getArtSale.");
            setError("Failed to retrieve art sale data.");
            return;
          }

          // Get current user UID from Firebase Auth
          const currentUserId = auth.currentUser?.uid;

          if (!currentUserId) {
            console.warn("Current user is not authenticated.");
            setError("User is not authenticated. Please log in again.");
            return;
          }

          // Call checkAndAddVideo with art data and UID
          const { exists } = await checkAndAddVideo(
            artSaleResponse[0].art,
            currentUserId
          );

          if (!exists) {
            console.log("Video successfully added to Firebase.");
          } else {
            console.log("Video already exists in Firebase.");
          }

          // Now, the video should exist in Firebase, so set videoExists to true
          setVideoExists(true);

          // Fetch the video data and interactions again
          const newVideoDoc = await getDoc(videoRef);

          if (newVideoDoc.exists()) {
            const data = newVideoDoc.data();
            setVideoData(data);

            // Check if the current user is the owner of the video
            if (data.userId === currentUserId) {
              setIsOwner(true);
              // Fetch interactions from subcollection
              const interactionsRef = collection(
                db,
                "videos",
                videoId,
                "interactions"
              );
              const interactionsSnapshot = await getDocs(interactionsRef);
              const interactionsData = interactionsSnapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
              }));
              // Sort interactions by timestamp
              interactionsData.sort((a, b) => a.timestamp - b.timestamp);
              setInteractions(interactionsData);
            } else {
              setIsOwner(false);
            }
          } else {
            console.error("Failed to add video to Firebase.");
            setError("Failed to add video. Please try again later.");
          }
        }
      } catch (error) {
        console.error("Error verifying ownership or adding video:", error);
        setError("An unexpected error occurred. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    verifyOrAddVideo();
  }, [videoId, location.state]);

  // Function to delete an interaction
  const handleDeleteInteraction = (interactionId) => {
    setInteractions((prevInteractions) =>
      prevInteractions.filter((interaction) => interaction.id !== interactionId)
    );
  };

  // Function to add a new interaction
  const handleAddInteraction = (newInteraction) => {
    setInteractions(
      [...interactions, newInteraction].sort(
        (a, b) => a.timestamp - b.timestamp
      )
    );
  };

  // Function to open the Add Interaction modal
  const handleOpenModal = (timestamp) => {
    setInitialTimestamp(timestamp);
    setOpenModal(true);
  };

  // Function to close the Add Interaction modal
  const handleCloseModal = () => {
    setOpenModal(false);
  };

  // Function to handle video time updates
  const handleTimeUpdate = (time) => {
    console.log("handleTimeUpdate - time:", time, typeof time);
    setCurrentTime(time);
  };

  // Render loading state
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Render error state
  if (error) {
    return (
      <Container maxWidth="sm">
        <Box textAlign="center" mt={5}>
          <Alert severity="error" sx={{ fontSize: "1.2rem" }}>
            {error}
          </Alert>
        </Box>
      </Container>
    );
  }

  // Render warning if video does not exist
  if (!videoExists) {
    return (
      <Container maxWidth="sm">
        <Box textAlign="center" mt={5}>
          <Alert severity="warning" sx={{ fontSize: "1.2rem" }}>
            The video with ID <b>{videoId}</b> was not found. It may have been
            deleted or does not exist.
          </Alert>
        </Box>
      </Container>
    );
  }

  // Render permission error if user is not the owner
  if (!isOwner) {
    return (
      <Container maxWidth="sm">
        <Box textAlign="center" mt={5}>
          <Alert severity="error" sx={{ fontSize: "1.2rem" }}>
            You do not have permission to edit this video.
          </Alert>
        </Box>
      </Container>
    );
  }

  // Render the main UI if video exists and user is the owner
  return (
    <Container maxWidth="lg">
      <Box mt={5}>
        <Typography variant="h4" gutterBottom sx={{ fontFamily: "basic-sans" }}>
          Interactive Video Elements
        </Typography>
        {/* Video Player with Progress Bar */}
        <VideoPlayerWithProgressBar
          src={videoData.url}
          interactions={interactions}
          onAddInteraction={handleOpenModal}
          onTimeUpdate={handleTimeUpdate} // Pass currentTime up
        />
        {/* Add Interaction Button */}
        <Box mt={2} textAlign="center">
          <Button
            variant="contained"
            color="secondary"
            onClick={() => handleOpenModal(currentTime)} // Pass currentTime
            sx={{
              backgroundColor: theme.colors.green,
              fontFamily: "basic-sans",
              fontWeight: "700",
              color: theme.colors.white,
              borderRadius: "50px",
              padding: "10px 30px",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: theme.colors.blue,
                boxShadow: "none",
              },
            }}
          >
            Add Interaction
          </Button>
        </Box>
        {/* Add Interaction Modal */}
        <AddInteractionModal
          open={openModal}
          handleClose={handleCloseModal}
          videoId={videoId}
          onAddInteraction={handleAddInteraction}
          initialTimestamp={initialTimestamp} // Pass initialTimestamp
        />
        {/* Interaction List */}
        <Box mt={5}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{ fontFamily: "basic-sans", fontWeight: "700" }}
          >
            Interactions
          </Typography>
          {interactions.length === 0 ? (
            <Typography variant="body1" sx={{ fontFamily: "basic-sans" }}>
              No interactions added yet.
            </Typography>
          ) : (
            interactions.map((interaction) => (
              <InteractionItem
                key={interaction.id}
                interaction={interaction}
                videoId={videoId}
                onDelete={handleDeleteInteraction}
              />
            ))
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default VideoInteractions;
